// FRANCHISE CAMPAIGN COPY — READING A GOOGLE DRIVE LINK.
//
// ⚠️ SHARED, and it has to be. The SERVER routes a pasted link by host (a Drive
// link goes through the service account as a PDF, anything else through
// safeFetch as a web page); the CLIENT needs the same answer to know which links
// the distilled offer-facts block was read from, and therefore whether it has
// gone stale. Two implementations of "is this a Drive link" would let the
// workspace and the service disagree about what was read — which is exactly the
// kind of drift the AM has no way to see.
//
// A pure leaf: no I/O, no imports. `offerDocuments.ts` does the fetching.

// ---------------------------------------------------------------------------
// Parsing a Drive link (pure)
// ---------------------------------------------------------------------------

/**
 * A Drive file id. Drive ids are URL-safe base64-ish: letters, digits, `-`, `_`.
 * The length floor keeps a stray path segment ("view", "edit") from being read
 * as an id — those would 404 confusingly rather than being rejected here.
 */
const ID_PATTERN = /^[A-Za-z0-9_-]{16,}$/;

const PATH_PATTERNS = [
  /\/file\/d\/([A-Za-z0-9_-]+)/, //  /file/d/<id>/view
  /\/document\/d\/([A-Za-z0-9_-]+)/, //  Docs
  /\/presentation\/d\/([A-Za-z0-9_-]+)/,
  /\/spreadsheets\/d\/([A-Za-z0-9_-]+)/,
  /\/d\/([A-Za-z0-9_-]+)/, //  the short form some share dialogs produce
];

/**
 * Pull the file id out of whatever the Drive UI gave the AM.
 *
 * ⚠️ AMs PASTE WHATEVER THEY WERE HANDED — the open link, the share link, the
 * "copy link" from the file list, occasionally a bare id out of a spreadsheet.
 * Handling only one shape means the others fail as "we couldn't read that file",
 * which reads as a permissions problem and sends the AM to fix the wrong thing.
 *
 * Returns null when there is no id in the string at all.
 */
export function parseDriveFileId(input: string): string | null {
  const value = (input ?? '').trim();
  if (value === '') return null;

  // A bare id, pasted straight out of a URL bar or a sheet.
  if (ID_PATTERN.test(value)) return value;

  for (const pattern of PATH_PATTERNS) {
    const match = pattern.exec(value);
    const id = match?.[1];
    if (id && ID_PATTERN.test(id)) return id;
  }

  // ?id=<id> — the older download/export link shape.
  const query = /[?&]id=([A-Za-z0-9_-]+)/.exec(value);
  const queryId = query?.[1];
  if (queryId && ID_PATTERN.test(queryId)) return queryId;

  return null;
}

/**
 * Whether a pasted link should be routed to Drive rather than to the ordinary
 * web-page reference fetcher.
 *
 * ⚠️ Host-based, not id-based: a bare id is ambiguous with all sorts of strings,
 * and mis-routing an ordinary URL into Drive would produce a 404 instead of
 * reading the page. `parseDriveFileId` is deliberately more permissive than this
 * for the case where the AM is explicitly filling a Drive field.
 */
export function isDriveLink(input: string): boolean {
  const value = (input ?? '').trim().toLowerCase();
  if (value === '') return false;
  return (
    value.includes('drive.google.com') ||
    value.includes('docs.google.com') ||
    value.includes('drive.usercontent.google.com')
  );
}
